/* eslint-disable */
import { useState, useEffect } from 'react';
// @mui
import { useQueryClient } from 'react-query';
import { useNavigate, useOutletContext } from 'react-router-dom';
import {
  Grid,
  Container,
  Typography,
  Box,
  CircularProgress,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
  Button,
  Alert,
  AlertTitle,
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import toast from 'react-hot-toast';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PeopleIcon from '@mui/icons-material/People';
import QuizIcon from '@mui/icons-material/Quiz';
import { DailyQuotes } from '../../components/daily-quotes';
import { GoalsDialog } from '../../components/dialog';
import { getUserDetails, mergeTheGoals } from '../../utils';
import { useQueryGetTotalUsers } from '../../hooks/react-query/useData';
import { useMutationUpdateGoals } from '../../hooks/react-query/goalsManagement';
import { BarChart } from '../../sections/@dashboard/app';
import { useQueryGetTodayQuote } from '../../hooks/react-query/quotes';
import { queryKeys } from '../../hooks/react-query/queryKeys';

// ----------------------------------------------------------------------

export default function EdifierDashboard() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const userDetails = getUserDetails();

  const userRole = userDetails?.role?.toLowerCase();

  const dataContext = useOutletContext();
  const { data: todayQuote } = useQueryGetTodayQuote();
  const [goalsModalData, setGoalsModalData] = useState({ open: false, goals: [], edifierName: '' });
  const [structuredGoals, setStructuredGoals] = useState({ goals: [], goalsCount: 0 });
  const [updatedGoalsPayload, setUpdatedGoalsPayload] = useState({});
  const [searchedValue, setSearchedValue] = useState('');

  const { data, isFetching: isLoading } = useQueryGetTotalUsers(
    userDetails?.token,
    navigate,
    userDetails?.role,
    ''
  );

  const { mutate: submitGoals, isLoading: isSubmittingGoals } = useMutationUpdateGoals(
    updatedGoalsPayload,
    toast,
    userDetails?.token,
    queryClient,
    setGoalsModalData,
    userRole
  );

  // Invalidate queries on mount to fetch fresh data
  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: [queryKeys.totalUsers] });
    queryClient.invalidateQueries({ queryKey: [queryKeys.getTodayQuote] });
    queryClient.invalidateQueries({ queryKey: [queryKeys.getUpcomingMeetingsList] });
  }, []);

  useEffect(() => {
    if (data?.data?.data?.goals && Array.isArray(data?.data?.data?.goals)) {
      restructureGoalsForMapping();
    }
  }, [data?.data?.data?.goals]);

  useEffect(() => {
    if (Object.keys(updatedGoalsPayload).length > 0) {
      submitGoals();
    }
  }, [updatedGoalsPayload]);

  const pushEntriesInArray = (
    mergedObjects,
    filteredArray,
    mergedObjectsIncompleteGoals,
    filteredArrayIncompleteGoals
  ) => {
    Object.keys(mergedObjects).forEach((key, index) => {
      filteredArray.push({ id: index, ...mergedObjects[key] });
    });

    Object.keys(mergedObjectsIncompleteGoals).forEach((key, index) => {
      filteredArrayIncompleteGoals.push({ id: index, ...mergedObjectsIncompleteGoals[key] });
    });
  };

  //Mapping Goals Data to Merge The goals
  const mapGoalsData = (mergedObjects, mergedObjectsIncompleteGoals) => {
    if (!Array.isArray(data?.data?.data?.goals)) {
      return; // Exit if goals is not an array
    }
    data?.data?.data?.goals?.forEach((mainObj, index) => {
      if (!mainObj?.goals && mainObj?.goal) {
        mergeTheGoals(mainObj, mergedObjects, mergedObjectsIncompleteGoals, mainObj?.goal, userDetails, index);
      } else {
        mainObj?.goals?.map((obj) => {
          mergeTheGoals(mainObj, mergedObjects, mergedObjectsIncompleteGoals, obj, userDetails, index);
        });
      }
    });
  };
  //main function for performing merge and adding entries

  const restructureGoalsForMapping = () => {
    const mergedObjects = {};
    const mergedObjectsIncompleteGoals = {};
    const filteredArray = [];
    const filteredArrayIncompleteGoals = [];

    mapGoalsData(mergedObjects, mergedObjectsIncompleteGoals);

    pushEntriesInArray(mergedObjects, filteredArray, mergedObjectsIncompleteGoals, filteredArrayIncompleteGoals);

    setStructuredGoals({ goals: [...filteredArray], inCompleteGoals: [...filteredArrayIncompleteGoals] });
  };

  //search from the record
  const searchGoalsFromRecord = () => {
    if (searchedValue === '' || searchedValue === null) {
      if (goalsModalData?.isCompleted) {
        setGoalsModalData({ ...goalsModalData, goals: structuredGoals?.goals });
      } else {
        setGoalsModalData({ ...goalsModalData, goals: structuredGoals?.inCompleteGoals });
      }
    }

    const mergedObjects = {};
    const mergedObjectsIncompleteGoals = {};
    const filteredArray = [];
    const filteredArrayIncompleteGoals = [];

    mapGoalsData(mergedObjects, mergedObjectsIncompleteGoals);

    pushEntriesInArray(mergedObjects, filteredArray, mergedObjectsIncompleteGoals, filteredArrayIncompleteGoals);

    const searchFilterCompleteGoals =
      filteredArray?.length === 0 ? [] : filteredArray?.filter((obj) => obj.name.includes(searchedValue));

    const searchFilterIncompleteGoals =
      filteredArrayIncompleteGoals.length === 0
        ? []
        : filteredArrayIncompleteGoals?.filter((obj) => obj.name.toLowerCase().includes(searchedValue.toLowerCase()));

    setStructuredGoals({ goals: [...searchFilterCompleteGoals], inCompleteGoals: [...searchFilterIncompleteGoals] });

    if (goalsModalData?.isCompleted) {
      setGoalsModalData({
        ...goalsModalData,
        goals: searchFilterCompleteGoals,
      });
    } else {
      setGoalsModalData({
        ...goalsModalData,
        goals: searchFilterIncompleteGoals,
      });
    }
  };

  // Calculate stats directly from raw goals data
  const rawGoals = data?.data?.data?.goals || [];
  const totalGoals = rawGoals.length;
  const completedGoalsCount = rawGoals.filter((goal) => goal?.goal?.isCompleted === true).length;
  const activeGoalsCount = rawGoals.filter((goal) => goal?.goal?.isCompleted === false).length;
  const progressPercentage = totalGoals > 0 ? Math.round((completedGoalsCount / totalGoals) * 100) : 0;

  // Get distinct mentees (teachers) from active goals for display
  const getDistinctMenteesFromGoals = () => {
    const activeGoals = rawGoals.filter((goal) => goal?.goal?.isCompleted === false);
    const menteeMap = new Map();
    
    activeGoals.forEach((goal) => {
      const mentee = goal?.goalsFor;
      if (mentee && typeof mentee === 'object' && mentee._id) {
        const menteeId = mentee._id;
        if (!menteeMap.has(menteeId)) {
          const menteeName = mentee.fname && mentee.lname
            ? `${mentee.fname} ${mentee.lname}`
            : mentee.fname || mentee.email || 'Mentee';
          menteeMap.set(menteeId, {
            _id: menteeId,
            name: menteeName,
            image: mentee.image || '',
            fname: mentee.fname || '',
            lname: mentee.lname || '',
          });
        }
      }
    });
    
    return Array.from(menteeMap.values());
  };

  const distinctMentees = getDistinctMenteesFromGoals();

  // Get survey data with user images and names
  const surveyData = queryClient.getQueryData(['totalUsers'], { exact: false })?.data?.data;
  const surveys = surveyData?.surveys || [];
  const surveysCount = surveys.length || 0;

  // Get upcoming meetings - check multiple possible data structures
  const meetingsResponse = dataContext?.[2]?.data?.data || dataContext?.[2]?.data || [];
  const meetingsData = Array.isArray(meetingsResponse) ? meetingsResponse : meetingsResponse?.meetings || [];
  const pendingSurveySubmissions = Array.isArray(meetingsResponse?.pendingSurveySubmissions)
    ? meetingsResponse.pendingSurveySubmissions
    : [];
  const upcomingMeetings = Array.isArray(meetingsData)
    ? meetingsData
        .filter((meeting) => {
          if (!meeting || !meeting.scheduleTime) return false;
          const meetingDate = new Date(meeting.scheduleTime);
          const now = new Date();
          now.setHours(0, 0, 0, 0);
          return meetingDate >= now;
        })
        .sort((a, b) => new Date(a.scheduleTime) - new Date(b.scheduleTime))
        .slice(0, 5)
    : [];

  // Format survey responses with user data
  const surveyResponses = surveys.slice(0, 4).map((survey) => {
    const userName = survey?.user ? `${survey.user.fname || ''} ${survey.user.lname || ''}`.trim() : 'Mentee';
    const userImage = survey?.user?.image || '';
    const timestamp = survey?.timestamp ? new Date(survey.timestamp) : (survey?.createdAt ? new Date(survey.createdAt) : new Date());
    return {
      ...survey,
      userName,
      userImage,
      timestamp,
    };
  });
  

  return (
    <>
      <Helmet>
        <title>Edifying Teachers | Dashboard</title>
      </Helmet>

      <Container maxWidth="xl" sx={{ p: '0 !important' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexDirection: { xs: 'column', md: 'row' },
            mb: 3,
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="mainTitles" sx={{ mb: '5px', color: 'text.titles', display: 'block' }}>
              Welcome back, {userDetails?.fname + ' ' + userDetails?.lname || 'Edifier'}!
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Track your mentees' progress and engagement
            </Typography>
          </Box>
        </Box>

        {todayQuote && <DailyQuotes quote={todayQuote} />}
        {pendingSurveySubmissions.length > 0 && (
          <Alert
            severity="warning"
            sx={{ mt: 2, mb: 2, borderRadius: 2 }}
            action={
              <Button color="warning" variant="contained" onClick={() => navigate('/dashboard/upcoming-events')}>
                Complete Survey
              </Button>
            }
          >
            <AlertTitle>Pending Survey Submission</AlertTitle>
            You have {pendingSurveySubmissions.length} meeting survey submission
            {pendingSurveySubmissions.length > 1 ? 's' : ''} pending.
          </Alert>
        )}

        {isLoading && (
          <Box sx={{ textAlign: 'center', my: '20px' }}>
            <CircularProgress />
          </Box>
        )}

        {!isLoading && (
          <>
          <Grid container spacing={3} sx={{ mt: 1 }} alignItems="stretch">
            {/* Stats Cards Row */}
            <Grid item xs={12} sm={6} md={3}>
              <Card
                sx={{
                  p: 2.5,
                  bgcolor: 'background.paper',
                  boxShadow: (theme) => theme.palette.mode === 'dark' ? '0px 2px 6px 0px rgba(0, 0, 0, 0.3)' : '0px 2px 6px 0px rgba(13, 10, 44, 0.08)',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'primary.lighter',
                    }}
                  >
                    <PeopleIcon sx={{ fontSize: 32, color: 'primary.main' }} />
                  </Box>
                  <Box>
                    <Typography variant="h3" sx={{ fontWeight: 600, mb: 0.5, color: 'text.titles' }}>
                      {data?.data?.data?.sessions || 0}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                      Total Sessions
                    </Typography>
                  </Box>
                </Box>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card
                sx={{
                  p: 2.5,
                  bgcolor: 'background.paper',
                  boxShadow: (theme) => theme.palette.mode === 'dark' ? '0px 2px 6px 0px rgba(0, 0, 0, 0.3)' : '0px 2px 6px 0px rgba(13, 10, 44, 0.08)',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'success.lighter',
                    }}
                  >
                    <CheckCircleIcon sx={{ fontSize: 32, color: 'success.main' }} />
                  </Box>
                  <Box>
                    <Typography variant="h3" sx={{ fontWeight: 600, mb: 0.5, color: 'text.titles' }}>
                      {completedGoalsCount}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                      Completed Goals
                    </Typography>
                  </Box>
                </Box>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card
                sx={{
                  p: 2.5,
                  bgcolor: 'background.paper',
                  boxShadow: (theme) => theme.palette.mode === 'dark' ? '0px 2px 6px 0px rgba(0, 0, 0, 0.3)' : '0px 2px 6px 0px rgba(13, 10, 44, 0.08)',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'warning.lighter',
                    }}
                  >
                    <AssignmentIcon sx={{ fontSize: 32, color: 'warning.main' }} />
                  </Box>
                  <Box>
                    <Typography variant="h3" sx={{ fontWeight: 600, mb: 0.5, color: 'text.titles' }}>
                      {activeGoalsCount}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                      Active Goals
                    </Typography>
                  </Box>
                </Box>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card
                sx={{
                  p: 2.5,
                  bgcolor: 'background.paper',
                  boxShadow: (theme) => theme.palette.mode === 'dark' ? '0px 2px 6px 0px rgba(0, 0, 0, 0.3)' : '0px 2px 6px 0px rgba(13, 10, 44, 0.08)',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'error.lighter',
                    }}
                  >
                    <QuizIcon sx={{ fontSize: 32, color: 'error.main' }} />
                  </Box>
                  <Box>
                    <Typography variant="h3" sx={{ fontWeight: 600, mb: 0.5, color: 'text.titles' }}>
                      {surveysCount}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                      Survey Responses
                    </Typography>
                  </Box>
                </Box>
              </Card>
            </Grid>

            {/* Main Content Area */}
            <Grid item xs={12} lg={7}>
              {/* Goals Progress Card */}
              <Card
                sx={{
                  boxShadow: (theme) => theme.palette.mode === 'dark' ? '0px 4px 20px rgba(0, 0, 0, 0.4)' : '0px 4px 20px rgba(0, 0, 0, 0.08)',
                  borderRadius: 3,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  bgcolor: 'background.paper',
                }}
              >
                <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', flex: 1, pb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.titles' }}>
                      Mentee Goals Progress
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() =>
                        setGoalsModalData({
                          display: true,
                          goals: data?.data?.data?.goals || [],
                        })
                      }
                    >
                      View All Goals
                    </Button>
                  </Box>

                  {/* Progress Bar */}
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Overall Progress
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                        {completedGoalsCount} of {totalGoals} completed
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={progressPercentage}
                      sx={{
                        height: 10,
                        borderRadius: 5,
                        bgcolor: 'action.hover',
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 5,
                          background: (theme) => `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary?.main || theme.palette.primary.dark} 100%)`,
                        },
                      }}
                    />
                  </Box>

                  {/* Goals List - Show distinct mentees */}
                  <Box sx={{ maxHeight: 300, overflowY: 'auto', flex: 1 }}>
                    {distinctMentees.length > 0 ? (
                      <List>
                        {distinctMentees.slice(0, 5).map((mentee, index) => {
                          // Get goals for this mentee
                          const menteeGoals = rawGoals.filter(
                            (goal) =>
                              goal?.goalsFor?._id === mentee._id && goal?.goal?.isCompleted === false
                          );
                          const goalTitles = menteeGoals
                            .map((g) => g?.goal?.title || 'Untitled Goal')
                            .join(', ');
                          
                          return (
                            <Box key={mentee._id || index}>
                              <ListItem
                                sx={{
                                  px: 2,
                                  py: 1.5,
                                  '&:hover': { backgroundColor: 'action.hover', borderRadius: 1 },
                                }}
                              >
                                <ListItemAvatar sx={{ minWidth: 40 }}>
                                  <Avatar
                                    src={mentee.image}
                                    sx={{
                                      bgcolor: 'primary.lighter',
                                      width: 32,
                                      height: 32,
                                    }}
                                  >
                                    {!mentee.image && (mentee.fname?.charAt(0) || mentee.name?.charAt(0) || 'M')}
                                  </Avatar>
                                </ListItemAvatar>
                                <ListItemText
                                  primary={mentee.name}
                                  secondary={goalTitles || 'No active goals'}
                                  primaryTypographyProps={{ fontWeight: 500 }}
                                  secondaryTypographyProps={{ 
                                    sx: { 
                                      display: '-webkit-box',
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: 'vertical',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                    } 
                                  }}
                                />
                              </ListItem>
                              {index < Math.min(4, distinctMentees.length - 1) && <Divider />}
                            </Box>
                          );
                        })}
                      </List>
                    ) : (
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          No active goals at the moment
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Right Sidebar */}
            <Grid item xs={12} lg={5}>
              {/* Upcoming Meetings Widget */}
              <Card
                sx={{
                  boxShadow: (theme) => theme.palette.mode === 'dark' ? '0px 4px 20px rgba(0, 0, 0, 0.4)' : '0px 4px 20px rgba(0, 0, 0, 0.08)',
                  borderRadius: 3,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  bgcolor: 'background.paper',
                }}
              >
                <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', flex: 1, pb: 3, minHeight: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box
                      component="img"
                      src="/assets/dashboard/scheduleIcon.svg"
                      alt="Schedule"
                      sx={{ mr: 1, width: 24, height: 24 }}
                    />
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.titles' }}>
                      Upcoming Meetings
                    </Typography>
                  </Box>
                  {upcomingMeetings.length > 0 ? (
                    <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
                      <List>
                        {upcomingMeetings.map((meeting, index) => {
                          const meetingDate = new Date(meeting.scheduleTime);
                          const today = new Date();
                          const isToday = meetingDate.toDateString() === today.toDateString();
                          const tomorrow = new Date(today);
                          tomorrow.setDate(tomorrow.getDate() + 1);
                          const isTomorrow = meetingDate.toDateString() === tomorrow.toDateString();

                          const participantName = meeting.fname && meeting.lname
                            ? `${meeting.fname || ''} ${meeting.lname || ''}`.trim()
                            : meeting.participantName || 'Participant';
                          const participantImage = meeting.image || '';

                          return (
                            <Box key={meeting._id || index}>
                              <ListItem
                                sx={{
                                  px: 0,
                                  py: 1.5,
                                  '&:hover': { backgroundColor: 'action.hover', borderRadius: 1 },
                                }}
                              >
                                <ListItemAvatar>
                                  <Avatar
                                    src={participantImage}
                                    sx={{
                                      width: 40,
                                      height: 40,
                                      bgcolor: 'primary.lighter',
                                    }}
                                  >
                                    {!participantImage && (participantName?.charAt(0) || 'P')}
                                  </Avatar>
                                </ListItemAvatar>
                                <ListItemText
                                  primary={
                                    <Box>
                                      <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
                                        {meeting.name}
                                      </Typography>
                                      <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                                        {participantName}
                                      </Typography>
                                    </Box>
                                  }
                                  secondary={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                      <Chip
                                        label={
                                          isToday
                                            ? 'Today'
                                            : isTomorrow
                                            ? 'Tomorrow'
                                            : meetingDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                                        }
                                        size="small"
                                        color={isToday ? 'primary' : 'default'}
                                      />
                                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                        {meetingDate.toLocaleTimeString('en-US', {
                                          hour: 'numeric',
                                          minute: '2-digit',
                                        })}
                                      </Typography>
                                    </Box>
                                  }
                                />
                              </ListItem>
                              {index < upcomingMeetings.length - 1 && <Divider />}
                            </Box>
                          );
                        })}
                      </List>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: 100,
                      }}
                    >
                      <Box
                        component="img"
                        src="/assets/dashboard/scheduleIcon.svg"
                        alt="Schedule"
                        sx={{ width: 64, height: 64, mb: 2, opacity: 0.5 }}
                      />
                      <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 500, mb: 0.5 }}>
                        No upcoming meetings
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        You don't have any meetings scheduled
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>

            </Grid>

            {/* Survey Responses Card - Full Width */}
            <Grid item xs={12}>
              <Card
                sx={{
                  boxShadow: (theme) => theme.palette.mode === 'dark' ? '0px 4px 20px rgba(0, 0, 0, 0.4)' : '0px 4px 20px rgba(0, 0, 0, 0.08)',
                  borderRadius: 3,
                  bgcolor: 'background.paper',
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <QuizIcon sx={{ mr: 1, color: 'primary.main' }} />
                      <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.titles' }}>
                        Recent Survey Responses
                      </Typography>
                    </Box>
                  </Box>

                  {surveyResponses.length > 0 ? (
                    <List>
                      {surveyResponses.map((survey, index) => (
                        <Box key={survey._id || index}>
                          <ListItem
                            sx={{
                              px: 0,
                              py: 1.5,
                              '&:hover': { backgroundColor: 'action.hover', borderRadius: 1 },
                            }}
                          >
                            <ListItemAvatar>
                              <Avatar
                                src={survey.userImage}
                                sx={{
                                  bgcolor: 'success.light',
                                  width: 40,
                                  height: 40,
                                }}
                              >
                                {!survey.userImage && (survey.userName?.charAt(0) || 'M')}
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={survey.userName || 'Mentee Survey'}
                              secondary={
                                survey.timestamp
                                  ? survey.timestamp.toLocaleDateString('en-US', {
                                      month: 'long',
                                      day: 'numeric',
                                      year: 'numeric',
                                    })
                                  : 'Recent response'
                              }
                              primaryTypographyProps={{ fontWeight: 500 }}
                            />
                            <Chip label="Completed" color="success" size="small" />
                          </ListItem>
                          {index < surveyResponses.length - 1 && <Divider />}
                        </Box>
                      ))}
                    </List>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        No survey responses yet
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* M's & C's Chart - Full Width */}
          {data?.data?.data?.MsCs && (
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <BarChart data={data?.data?.data?.MsCs} toast={toast} />
              </Grid>
            </Grid>
          )}
          </>
        )}

        <GoalsDialog
          goals={goalsModalData?.goals || []}
          open={goalsModalData?.display || false}
          handleClose={() => setGoalsModalData({ display: false, goals: [] })}
          setUpdatedGoalsPayload={setUpdatedGoalsPayload}
          updatedGoalsPayload={updatedGoalsPayload}
          isSubmittingGoals={isSubmittingGoals}
          searchedValue={searchedValue}
          searchFromRecord={searchGoalsFromRecord}
          isTeacher={false}
          setSearchedValue={setSearchedValue}
        />
      </Container>
    </>
  );
}
